import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Grid, Paper, LinearProgress, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';

interface PasswordStrength {
  score: number;
  level: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong';
  color: 'error' | 'warning' | 'info' | 'success';
  feedback: string[];
}

interface PasswordCriteria {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumbers: boolean;
  hasSymbols: boolean;
  hasNoRepeats: boolean;
  hasNoCommonPatterns: boolean;
}

export default function PasswordStrengthChecker() {
  const [password, setPassword] = useState<string>('');
  const [strength, setStrength] = useState<PasswordStrength | null>(null);
  const [criteria, setCriteria] = useState<PasswordCriteria>({
    hasMinLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumbers: false,
    hasSymbols: false,
    hasNoRepeats: false,
    hasNoCommonPatterns: false
  });

  const checkPasswordStrength = (pwd: string): PasswordStrength => {
    if (!pwd) {
      return { score: 0, level: 'Very Weak', color: 'error', feedback: [] };
    }

    let score = 0;
    const feedback: string[] = [];

    // Length check
    if (pwd.length >= 8) score += 1;
    else feedback.push('Use at least 8 characters');

    if (pwd.length >= 12) score += 1;
    else if (pwd.length >= 8) feedback.push('Consider using 12+ characters for better security');

    // Character variety
    if (/[A-Z]/.test(pwd)) score += 1;
    else feedback.push('Include uppercase letters (A-Z)');

    if (/[a-z]/.test(pwd)) score += 1;
    else feedback.push('Include lowercase letters (a-z)');

    if (/[0-9]/.test(pwd)) score += 1;
    else feedback.push('Include numbers (0-9)');

    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    else feedback.push('Include special characters (!@#$%^&*)');

    // Pattern checks
    if (!/(.)\1{2,}/.test(pwd)) score += 1;
    else feedback.push('Avoid repeating characters (aaa, 111)');

    if (!/(?:012|123|234|345|456|567|678|789|890|abc|bcd|cde|def)/.test(pwd.toLowerCase())) score += 1;
    else feedback.push('Avoid sequential characters (123, abc)');

    // Common patterns
    const commonPatterns = ['password', '123456', 'qwerty', 'admin', 'login'];
    if (!commonPatterns.some(pattern => pwd.toLowerCase().includes(pattern))) score += 1;
    else feedback.push('Avoid common words or patterns');

    // Determine level
    let level: PasswordStrength['level'];
    let color: PasswordStrength['color'];

    if (score <= 2) {
      level = 'Very Weak';
      color = 'error';
    } else if (score <= 4) {
      level = 'Weak';
      color = 'error';
    } else if (score <= 6) {
      level = 'Fair';
      color = 'warning';
    } else if (score <= 7) {
      level = 'Good';
      color = 'info';
    } else {
      level = 'Strong';
      color = 'success';
    }

    return { score, level, color, feedback };
  };

  const checkCriteria = (pwd: string): PasswordCriteria => {
    return {
      hasMinLength: pwd.length >= 8,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumbers: /[0-9]/.test(pwd),
      hasSymbols: /[^A-Za-z0-9]/.test(pwd),
      hasNoRepeats: !/(.)\1{2,}/.test(pwd),
      hasNoCommonPatterns: !/(?:012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|password|123456|qwerty|admin|login)/.test(pwd.toLowerCase())
    };
  };

  useEffect(() => {
    setStrength(checkPasswordStrength(password));
    setCriteria(checkCriteria(password));
  }, [password]);

  const CriteriaItem = ({ met, children }: { met: boolean; children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      {met ? (
        <CheckCircleIcon color="success" sx={{ mr: 1, fontSize: 20 }} />
      ) : (
        <CancelIcon color="error" sx={{ mr: 1, fontSize: 20 }} />
      )}
      <Typography variant="body2" color={met ? 'success.main' : 'error.main'}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Password Strength Checker</Typography>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Enter a password to check its strength and get security recommendations.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Enter Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="Type your password here..."
            />
          </Grid>

          {strength && password && (
            <>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2">
                      Password Strength:
                    </Typography>
                    <Chip 
                      label={strength.level}
                      color={strength.color}
                      size="small"
                      icon={strength.score >= 7 ? <CheckCircleIcon /> : <WarningIcon />}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={(strength.score / 9) * 100}
                    color={strength.color}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Score: {strength.score}/9
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Security Criteria
                  </Typography>
                  <CriteriaItem met={criteria.hasMinLength}>
                    At least 8 characters long
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasUppercase}>
                    Contains uppercase letters (A-Z)
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasLowercase}>
                    Contains lowercase letters (a-z)
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasNumbers}>
                    Contains numbers (0-9)
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasSymbols}>
                    Contains special characters
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasNoRepeats}>
                    No repeating characters
                  </CriteriaItem>
                  <CriteriaItem met={criteria.hasNoCommonPatterns}>
                    No common patterns or words
                  </CriteriaItem>
                </Paper>
              </Grid>

              {strength.feedback.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper elevation={1} sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Recommendations
                    </Typography>
                    {strength.feedback.map((item, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                        • {item}
                      </Typography>
                    ))}
                  </Paper>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2, backgroundColor: '#e8f5e8', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Password Security Best Practices
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Use a unique password for each account<br/>
                • Consider using a passphrase with multiple words<br/>
                • Enable two-factor authentication when available<br/>
                • Use a reputable password manager<br/>
                • Regularly update passwords for sensitive accounts<br/>
                • Never share passwords or store them in plain text
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}
